import { NavLink } from 'react-router-dom'
import { useNavBadges, type BadgeValue } from '@/hooks/useNavBadges'
import { useGameStore } from '@/store/gameStore'
import { COACH_HIDDEN_ROUTES } from '@/config/gameModesConfig'
import { MusicMiniPlayer } from '../components/MusicMiniPlayer'
import { NavBadge } from '../components/NavBadge'
import {
  LayoutDashboard,
  Home,
  Building2,
  Users,
  Briefcase,
  Store,
  Wallet,
  Trophy,
  Settings,
} from 'lucide-react'

const NAV_ITEMS = [
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/club', label: 'Mi Club', icon: Home },
  { to: '/facilities', label: 'Instalaciones', icon: Building2 },
  { to: '/staff', label: 'Personal', icon: Users },
  { to: '/services', label: 'Servicios', icon: Store },
  { to: '/economy', label: 'Economia', icon: Wallet },
  { to: '/competitions', label: 'Competicion', icon: Trophy },
  { to: '/career', label: 'Carrera', icon: Briefcase },
] as const

const BOTTOM_ITEMS = [
  { to: '/settings', label: 'Ajustes', icon: Settings },
] as const

interface SidebarProps {
  collapsed: boolean
}

export function Sidebar({ collapsed }: SidebarProps) {
  const badges = useNavBadges()
  const gameMode = useGameStore(s => s.gameMode)
  const items = gameMode === 'coach'
    ? NAV_ITEMS.filter(i => !COACH_HIDDEN_ROUTES.has(i.to))
    : NAV_ITEMS

  return (
    <aside
      style={{
        width: collapsed ? 'var(--sidebar-collapsed)' : 'var(--sidebar-width)',
        background: 'var(--glass-bg)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        borderRight: '1px solid var(--glass-border)',
        position: 'fixed',
        top: 'var(--header-height)',
        left: 0,
        bottom: 0,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        transition: 'width 0.2s ease',
        overflow: 'hidden',
        zIndex: 40,
      }}
    >
      <nav
        className="tx-perforations"
        style={{ padding: '0.6rem 0.5rem', display: 'flex', flexDirection: 'column', gap: '3px' }}
      >
        {items.map(item => (
          <SidebarLink
            key={item.to}
            {...item}
            collapsed={collapsed}
            badge={badges[item.to as keyof typeof badges]}
          />
        ))}
      </nav>

      <div style={{ borderTop: '1px solid var(--glass-border)', padding: '0.5rem 0.4rem 0.4rem' }}>
        <MusicMiniPlayer variant="sidebar" collapsed={collapsed} />
        <nav>
          {BOTTOM_ITEMS.map(item => (
            <SidebarLink key={item.to} {...item} collapsed={collapsed} badge={false} />
          ))}
        </nav>
      </div>
    </aside>
  )
}

interface SidebarLinkProps {
  to: string
  label: string
  icon: React.ComponentType<{ size?: number | string }>
  collapsed: boolean
  badge: BadgeValue
}

function SidebarLink({ to, label, icon: Icon, collapsed, badge }: SidebarLinkProps) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `nav-angled${isActive ? ' active' : ''}${collapsed ? ' collapsed' : ''}`
      }
    >
      <span style={{ position: 'relative', display: 'inline-flex' }}>
        <Icon size={20} />
        <NavBadge value={badge} />
      </span>
      {!collapsed && <span>{label}</span>}
    </NavLink>
  )
}
